import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import usePopup from '../../../hooks/usePopup';
import ScorecardForm from '../../ScorecardsIndex/ScorecardForm';
import { buildShowGlobalKpiList } from '../../../navigation/appRoutes';
import ScorecardDeletionConfirmation from '../../../components/Scorecard/ScorecardDeletionConfirmation';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import ContextMenu, { Option, Info, Break } from 'kingpin/atoms/ContextMenu';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import V3UnpublishScorecard from './V3UnpublishScorecard';
import ContentUsage from '../../../components/ContentUsage';
import FleetOpsStaffOnly from '../../../components/Common/FleetOpsStaffOnly';
import useReportAnIssueModal from '../../../components/ReportAnIssue/useReportAnIssueModal';
import ScorecardQuickFiltersModal from '../../../components/Scorecard/QuickFilters';

const ScorecardMoreMenuContainer = ({
  scorecard,
  isEditOnly,
  isEditHidden,
  isQuickFiltersEnabled,
}: {
  scorecard: Scorecards.Scorecard;
  isEditOnly?: boolean;
  isEditHidden?: boolean;
  // Only enabled from ScorecardShow, which renders this inside ScorecardProvider
  // (so ScorecardContext is available). Other usages sit outside the provider.
  isQuickFiltersEnabled?: boolean;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { trackEvent } = useContext(AnalyticsContext);

  const navigate = useNavigate();
  const { isOpen, open, close } = usePopup();
  const {
    isOpen: isShowingDeleteConfirmation,
    open: openDeleteConfirmation,
    close: closeDeleteConfirmation,
  } = usePopup();
  const { isOpen: isCopyOpen, open: openCopy, close: closeCopy } = usePopup();
  const {
    isOpen: isEditFormOpen,
    open: openEditForm,
    close: closeEditForm,
  } = usePopup();
  const {
    isOpen: isUsageOpen,
    open: openUsage,
    close: closeUsage,
  } = usePopup();
  const {
    isOpen: isQuickFiltersOpen,
    open: openQuickFilters,
    close: closeQuickFilters,
  } = usePopup();
  const { open: openReportAnIssue, modal: reportAnIssueModal } =
    useReportAnIssueModal({
      type: 'scorecard',
      gadgetName: scorecard.title,
    });

  const onEditClicked = useCallback(() => {
    openEditForm();
    trackEvent('KPI List - Edit Clicked', {
      scorecardName: scorecard.title,
      scorecardId: scorecard.id,
    });
  }, [openEditForm, scorecard.id, scorecard.title, trackEvent]);

  const onCopyClicked = useCallback(() => {
    openCopy();
    trackEvent('KPI List - Copy Clicked', {
      scorecardName: scorecard.title,
      scorecardId: scorecard.id,
    });
  }, [openCopy, scorecard.id, scorecard.title, trackEvent]);

  const onDeleteClicked = useCallback(() => {
    openDeleteConfirmation();
    trackEvent('KPI List - Delete Clicked', {
      scorecardName: scorecard.title,
      scorecardId: scorecard.id,
    });
  }, [openDeleteConfirmation, scorecard.id, scorecard.title, trackEvent]);

  const onScorecardCreated = useCallback(
    (newScorecardId: string) => {
      navigate(
        buildShowGlobalKpiList({
          scorecardId: newScorecardId,
          portal: selectedPortal,
        }),
      );
    },
    [navigate, selectedPortal],
  );

  return (
    <div className={isOpen ? '' : 'showOnMouseOver'}>
      <ContextMenu testId="scorecard-menu" onOpen={open} onClose={close}>
        <>
          {!isEditHidden && (
            <>
              <V3UnpublishScorecard scorecard={scorecard} />
              <Option onClick={onEditClicked} label="Edit KPI List" />
            </>
          )}
          {isQuickFiltersEnabled && (
            <Option onClick={openQuickFilters} label="Quick Filters" />
          )}
          {!isEditOnly && (
            <>
              <Option onClick={onCopyClicked} label={'Copy KPI List'} />
              <Option onClick={openUsage} label={'Show Usage'} />
              <Info {...scorecard} />
              <V3UnpublishScorecard scorecard={scorecard} />
              <FleetOpsStaffOnly>
                <Option
                  label="Report an issue"
                  onClick={openReportAnIssue}
                  testId="report-an-issue"
                />
              </FleetOpsStaffOnly>
              <Break />
              <Option
                onClick={onDeleteClicked}
                label={'Delete KPI List'}
                isDanger
              />
            </>
          )}
        </>
      </ContextMenu>
      {reportAnIssueModal}
      <ScorecardDeletionConfirmation
        scorecard={scorecard}
        isOpen={isShowingDeleteConfirmation}
        close={closeDeleteConfirmation}
      />
      <ModalTransition>
        {isCopyOpen && (
          <Modal shouldScrollInViewport={false} autoFocus={false}>
            <ScorecardForm
              scorecard={scorecard}
              isCopyingScorecard
              close={closeCopy}
              onScorecardCreated={onScorecardCreated}
            />
          </Modal>
        )}
      </ModalTransition>
      <ContentUsage
        isOpen={isUsageOpen}
        close={closeUsage}
        type={'scorecard'}
      />

      {isQuickFiltersEnabled && (
        <ScorecardQuickFiltersModal
          isOpen={isQuickFiltersOpen}
          close={closeQuickFilters}
        />
      )}

      <ModalTransition>
        {isEditFormOpen && (
          <Modal shouldScrollInViewport={false} autoFocus={false}>
            <ScorecardForm close={closeEditForm} scorecard={scorecard} />
          </Modal>
        )}
      </ModalTransition>
    </div>
  );
};

export default ScorecardMoreMenuContainer;
